import {brlTextFromValue,brlTextFromDigits,brlAppendDigit,brlDeleteDigit,brlTextFromPaste} from './brlMoneyInput.js';
import React, { useEffect } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';

export function Button({ kind = 'primary', size = 'normal', children, className = '', ...props }) {
  return (
    <button className={`button ${kind} ${size} ${className}`.trim()} {...props}>
      {children}
    </button>
  );
}

export function Modal({ title, subtitle, onClose, children, footer, wide = false, className = '' }) {
  return (
    <div
      className={`backdrop ${className ? `${className}Backdrop` : ''}`.trim()}
      onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}
    >
      <section className={`modal ${wide ? 'wide' : '' } ${className}`.trim()} role="dialog" aria-modal="true">
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="iconButton" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </header>
        <div className="modalBody">{children}</div>
        {footer && <footer>{footer}</footer>}
      </section>
    </div>
  );
}

export function Notice({ message, tone = 'ok', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5500);
    return () => clearTimeout(timer);
  }, [message, onClose]);
  return (
    <div className={`notice ${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      {tone === 'bad' ? <AlertTriangle size={18} /> : <Check size={18} />}
      <span>{message}</span>
      <button onClick={onClose} aria-label="Fechar aviso">
        ×
      </button>
    </div>
  );
}

export function Brand({ compact = false }) {
  return (
    <div className={`brand ${compact ? 'compact' : ''}`}>
      <span>K</span>
      <div>
        <strong>K-SmartPDV</strong>
        <small>KAIZEN · OPERAÇÃO INTELIGENTE</small>
      </div>
    </div>
  );
}

export function Header({ eyebrow, title, description, action, meta }) {
  return (
    <header className="pageHeader">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {meta && <div className="headerMeta">{meta}</div>}
      </div>
      {action && <div className="headerActions">{action}</div>}
    </header>
  );
}

export function StatusBadge({ tone = 'neutral', children }) {
  return <span className={`statusBadge ${tone}`}>{children}</span>;
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="emptyState">
      {Icon && <Icon size={34} />}
      <strong>{title}</strong>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={className}>
      <span>{label}</span>
      {children}
      {hint && <small className="fieldHint">{hint}</small>}
    </label>
  );
}

export function MoneyInput({ value, onChange, ...props }) {
  const visible=brlTextFromValue(value);
  const isAllSelected=(input)=>input.selectionStart===0&&input.selectionEnd===input.value.length;
  const caretAtEnd=(input)=>{
    try { input.setSelectionRange(input.value.length,input.value.length) } catch {}
  };
  return (
    <div className="moneyInput" data-format="BRL-centavos">
      <span>R$</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9,.]*"
        autoComplete="off"
        aria-label={props['aria-label']}
        value={visible}
        onFocus={(e)=>caretAtEnd(e.currentTarget)}
        onClick={(e)=>{if(!isAllSelected(e.currentTarget))caretAtEnd(e.currentTarget)}}
        onKeyDown={(e)=>{
          if(e.ctrlKey||e.metaKey||e.altKey)return;
          const key=e.key;
          if(/^[0-9]$/.test(key)){
            e.preventDefault();
            onChange(brlAppendDigit(visible,key,isAllSelected(e.currentTarget)));
            caretAtEnd(e.currentTarget);
          }else if(key==='Backspace'||key==='Delete'){
            e.preventDefault();
            onChange(brlDeleteDigit(visible,isAllSelected(e.currentTarget)||key==='Delete'));
            caretAtEnd(e.currentTarget);
          }
        }}
        onPaste={(e)=>{
          e.preventDefault();
          onChange(brlTextFromPaste(e.clipboardData?.getData('text')||''));
          caretAtEnd(e.currentTarget);
        }}
        onChange={(e)=>{
          // Mobile IMEs can emit input without keydown; controlled fallback.
          const raw=e.currentTarget.value;
          onChange(brlTextFromDigits(raw));
          caretAtEnd(e.currentTarget);
        }}
        {...props}
      />
    </div>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="segmented">
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={value === option.value ? 'active' : ''}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function formatDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

